/* =====================================================================================
   The standing notice on a demonstration environment.

   Not a courtesy. Every commercial record in a demonstration tenant is invented, and several
   of them look exactly like real ones: contracts, payer negotiations, trial results.
   Somebody will screenshot this. The notice is what stops an invented proteinuria figure
   circulating as though it were real.

   COLLAPSIBLE, NOT DISMISSIBLE. The full sentence takes a line off the top of every screen,
   which is intrusive when the thing you want someone looking at is the market model. So it
   collapses to a small marker that stays visible and expands on click.

   It never disappears entirely, and the collapsed state lives in sessionStorage rather than
   localStorage, so it returns to full on the next visit. A permanent dismissal would mean a
   prospect who collapsed it in January sees no notice in March, which defeats having one.
   ===================================================================================== */

import { useState } from 'react';
import { TENANT } from '@tenant';
import './demobanner.css';

const KEY = 'forum.demoNotice.collapsed';

export function DemoBanner() {
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem(KEY) === '1');

  /* A sandbox is the second case: a real backend, so demo is false, and fictitious records, so
     the notice must still stand. A tenant that supplies demoNotice gets the banner whether or
     not it has a database; one that supplies neither is a real customer and gets nothing. */
  if (!TENANT.demo && !TENANT.demoNotice) return null;

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    sessionStorage.setItem(KEY, next ? '1' : '0');
  }

  if (collapsed) {
    return (
      <button className="demo-pill" onClick={toggle} aria-expanded={false}
              title="This is a demonstration environment. Click to read the full notice.">
        <span className="demo-dot" aria-hidden="true" />
        Demonstration
      </button>
    );
  }

  return (
    <div className="demo-banner" role="note">
      {/* The default below asserts fiction, which is right for an invented sales prospect and
          wrong for a market model about a real company built from public sources. A tenant of
          the second kind supplies its own wording; getting this backwards would either present
          invented records as real or dismiss sourced work as invented. */}
      <span className="demo-text">
        <strong>{TENANT.demoNotice?.headline ?? 'Demonstration.'}</strong>{' '}
        {TENANT.demoNotice?.body ?? (
          <>
            {TENANT.brand.orgName} is a fictitious company and every commercial record here is
            invented. The disease, the competing products and the market access mechanics are
            real and sourced.
          </>
        )}
      </span>
      <button className="demo-collapse" onClick={toggle} aria-expanded
              aria-label="Collapse this notice">
        Hide
      </button>
    </div>
  );
}
